import { useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, BadgeCheck, ChevronRight, Store } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'
import { useUpdateCompanyDetails } from '~/entities/business/model/use-business'
import { afterProfileSetup } from '~/entities/profile/lib/profile-setup'
import {
  activateNewProfile,
  useProfileScope,
  useProfileSetupState,
} from '~/entities/profile/model/use-profiles'
import { useCanEditCompanyDetails } from '~/features/company-details/use-can-edit-company-details'
import { CreateListingForm } from '~/features/listings/components/create-listing-form'
import {
  type PlaceSaveOutcome,
  PlaceWebsiteFlow,
} from '~/features/listings/website-start/place-website-flow'
import { OrganizerProfileEditor } from '~/features/organizer/components/organizer-profile-editor'
import * as m from '~/paraglide/messages'
import { ManualStartIcon } from '~/shared/ui/manual-start'
import { Skeleton } from '~/shared/ui/skeleton'
import { SetupFrame } from './setup-frame'

export type SetupKind = 'place' | 'organizer'

interface ProfileSetupPageProps {
  kind?: SetupKind
  manual?: boolean
}

function useLatched(value: boolean): boolean {
  const [latched, setLatched] = useState(value)
  if (value && !latched) setLatched(true)
  return latched || value
}

/**
 * Creating a profile, full screen and outside the dashboard: pick a place or an organizer, set it
 * up, then land in the new profile's own dashboard. A business without any profile goes through
 * it first, as step one of its setup.
 */
export function ProfileSetupPage({ kind, manual }: ProfileSetupPageProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { businessId, enabled, isLoading } = useProfileScope()
  const setup = useProfileSetupState()
  const updateCompanyDetails = useUpdateCompanyDetails(businessId)
  const canEditCompanyDetails = useCanEditCompanyDetails()
  const onboarding = useLatched(!setup.isLoading && setup.needsSetup)
  const onboardingStep = onboarding ? 1 : undefined

  if (isLoading || setup.isLoading || !businessId) {
    return (
      <SetupFrame>
        <Skeleton className="mx-auto h-96 w-full max-w-3xl rounded-xl" />
      </SetupFrame>
    )
  }

  if (!enabled && !onboarding) {
    return kind === 'organizer' ? (
      <Navigate to="/dashboard/organizer" search={{ new: true }} replace />
    ) : (
      <Navigate to="/dashboard/listings/create" search={{ manual }} replace />
    )
  }

  const enter = async (
    match: { listingId: string } | { profileId: string },
    page: 'home' | 'editPlace',
  ) => {
    const profile = await activateNewProfile(queryClient, businessId, match).catch(() => null)
    if (!profile) toast.info(m.setupProfileNotReady())
    const next =
      onboarding && canEditCompanyDetails ? afterProfileSetup(setup.business) : '/dashboard'
    if (onboarding && profile && next === '/setup/company') {
      await updateCompanyDetails.mutateAsync({ companyName: profile.name }).catch(() => undefined)
    }
    queryClient.resetQueries().catch(() => undefined)
    if (profile && page === 'editPlace' && 'listingId' in match) {
      navigate({
        to: '/dashboard/listings/$listingId/edit',
        params: { listingId: match.listingId },
      })
    } else {
      navigate({ to: next })
    }
  }

  const placeSaved = (listingId: string, outcome: PlaceSaveOutcome) =>
    enter({ listingId }, outcome === 'publishFailed' ? 'editPlace' : 'home')

  const back = (
    <Link
      to="/setup/profile"
      className="inline-flex items-center gap-1.5 text-muted-foreground text-sm transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      {m.back()}
    </Link>
  )

  if (kind === 'place' && manual) {
    return (
      <SetupFrame back={back} onboardingStep={onboardingStep}>
        <div className="mx-auto w-full max-w-5xl">
          <CreateListingForm
            onCreated={placeSaved}
            onCancel={() => navigate({ to: '/setup/profile', search: { kind: 'place' } })}
          />
        </div>
      </SetupFrame>
    )
  }

  if (kind === 'place') {
    return (
      <SetupFrame back={back} onboardingStep={onboardingStep}>
        <PlaceWebsiteFlow
          onSaved={placeSaved}
          manualLink={
            <Link to="/setup/profile" search={{ kind: 'place', manual: true }}>
              <ManualStartIcon />
              {m.placeWebManual()}
            </Link>
          }
        />
      </SetupFrame>
    )
  }

  if (kind === 'organizer') {
    return (
      <SetupFrame back={back} onboardingStep={onboardingStep}>
        <div className="mx-auto flex w-full max-w-2xl justify-center">
          <OrganizerProfileEditor
            creating
            onCreated={(profileId) => enter({ profileId }, 'home')}
          />
        </div>
      </SetupFrame>
    )
  }

  return (
    <SetupFrame onboardingStep={onboardingStep}>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 pt-6 text-center sm:pt-14">
        <div className="flex flex-col gap-3">
          <h1 className="text-balance font-semibold text-3xl text-foreground leading-[1.1] tracking-[-0.03em] sm:text-4xl">
            {onboarding ? m.onboardingProfileTitle() : m.setupProfileTitle()}
          </h1>
          <p className="mx-auto max-w-xl text-balance text-base text-muted-foreground sm:text-lg">
            {onboarding ? m.onboardingProfileSubtitle() : m.setupProfileSubtitle()}
          </p>
        </div>
        <div className="grid gap-4 text-left sm:grid-cols-2">
          <KindCard
            kind="place"
            icon={<Store className="h-6 w-6" />}
            title={onboarding ? m.onboardingPlaceTitle() : m.setupProfilePlaceTitle()}
            description={
              onboarding ? m.onboardingPlaceDescription() : m.setupProfilePlaceDescription()
            }
          />
          <KindCard
            kind="organizer"
            icon={<BadgeCheck className="h-6 w-6" />}
            title={onboarding ? m.onboardingOrganizerTitle() : m.setupProfileOrganizerTitle()}
            description={
              onboarding ? m.onboardingOrganizerDescription() : m.setupProfileOrganizerDescription()
            }
          />
        </div>
      </div>
    </SetupFrame>
  )
}

function KindCard({
  kind,
  icon,
  title,
  description,
}: {
  kind: SetupKind
  icon: ReactNode
  title: string
  description: string
}) {
  return (
    <Link
      to="/setup/profile"
      search={{ kind }}
      className="group flex items-start gap-4 rounded-xl border bg-card p-5 text-card-foreground beacon-interactive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50"
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-brand-subtle text-brand-subtle-foreground">
        {icon}
      </span>
      <span className="min-w-0 flex-1 space-y-1">
        <span className="block font-semibold text-lg tracking-tight">{title}</span>
        <span className="block text-muted-foreground text-sm">{description}</span>
      </span>
      <ChevronRight className="mt-3 h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-300 ease-spring group-hover:translate-x-0.5" />
    </Link>
  )
}
